/**
 * Auto-applies exactly one offer discount code at a time.
 * Gift banner: claim adds gift product + gift code; remove clears both.
 */
(function () {
  const STORAGE_KEY = 'offerNoticeGiftSelected';
  const GIFT_PROPERTY = '_gift_offer';

  const OfferNotice = {
    syncing: false,
    bound: false,

    getGiftSelected() {
      try {
        return sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch (error) {
        return false;
      }
    },

    setGiftSelected(selected) {
      try {
        sessionStorage.setItem(STORAGE_KEY, selected ? '1' : '0');
      } catch (error) {
        // ignore storage errors
      }
    },

    getConfig() {
      const offer = document.querySelector('#offer-notice .st-cart__offer:not(.st-cart__offer--gift)');
      const banner = document.getElementById('giftBanner');
      const settings = (window.theme && theme.settings && theme.settings.offerNotice) || {};

      if (!settings.enabled) return null;

      const giftEnabledAttr = offer && offer.dataset.giftEnabled;
      const giftEnabled = giftEnabledAttr != null
        ? giftEnabledAttr === 'true'
        : Boolean(settings.giftEnabled);

      const giftUnlockedAttr = offer && offer.dataset.giftUnlocked;
      const amount = offer ? Number(offer.dataset.offerAmount || 0) : 0;
      const giftMin = Number((offer && offer.dataset.giftMin) || settings.giftMin || 0);
      const giftCode = ((offer && offer.dataset.giftCode) || settings.giftCode || '').trim();
      const giftVariantId = Number(
        (banner && banner.dataset.giftVariantId)
        || (offer && offer.dataset.giftVariantId)
        || settings.giftVariantId
        || 0
      ) || null;
      const giftUnlocked = giftUnlockedAttr != null
        ? giftUnlockedAttr === 'true'
        : Boolean(giftEnabled && giftCode && giftMin > 0 && amount >= giftMin);

      return {
        enabled: true,
        qty: offer ? Number(offer.dataset.offerQty || 0) : 0,
        qty1: Number((offer && offer.dataset.offerQty1) || settings.qty1 || 0),
        qty2: Number((offer && offer.dataset.offerQty2) || settings.qty2 || 0),
        code1: ((offer && offer.dataset.offerCode1) || settings.code1 || '').trim(),
        code2: ((offer && offer.dataset.offerCode2) || settings.code2 || '').trim(),
        amount,
        giftEnabled,
        giftUnlocked,
        giftMin,
        giftCode,
        giftVariantId
      };
    },

    qtyCode(config) {
      if (!config) return '';
      if (config.qty >= config.qty2 && config.code2) return config.code2;
      if (config.qty >= config.qty1 && config.code1) return config.code1;
      return '';
    },

    desiredCode(config) {
      if (!config) return '';

      const qtyCode = this.qtyCode(config);
      const giftReady = config.giftEnabled && config.giftUnlocked && config.giftCode;
      const wantGift = giftReady && this.getGiftSelected();

      if (wantGift) return config.giftCode;
      if (qtyCode) return qtyCode;
      return '';
    },

    offerCodes(config) {
      return [config.code1, config.code2, config.giftCode]
        .filter(Boolean)
        .map((code) => code.toUpperCase())
        .filter((code, index, list) => list.indexOf(code) === index);
    },

    isGiftLineItem(item) {
      if (!item) return false;
      const props = item.properties || {};
      return props[GIFT_PROPERTY] === '1' || props[GIFT_PROPERTY] === 1 || props[GIFT_PROPERTY] === true;
    },

    findGiftLineItem(cart) {
      if (!cart || !cart.items) return null;
      return cart.items.find((item) => this.isGiftLineItem(item)) || null;
    },

    syncGiftUI(config) {
      const banner = document.getElementById('giftBanner');
      if (!banner || !config || !config.giftEnabled) return;

      const claimed = banner.querySelector('[data-gift-state="claimed"]');
      const ready = banner.querySelector('[data-gift-state="ready"]');
      const progress = banner.querySelector('[data-gift-state="progress"]');

      const giftReady = Boolean(config.giftUnlocked && config.giftCode);
      const selected = giftReady && this.getGiftSelected();

      if (claimed) claimed.hidden = !selected;
      if (ready) ready.hidden = !(giftReady && !selected);
      if (progress) progress.hidden = giftReady;

      banner.classList.toggle('is-claimed', selected);
      banner.classList.toggle('is-ready', giftReady && !selected);
    },

    async fetchCart() {
      const response = await fetch(`${theme.routes.cart}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(response.status);
      return response.json();
    },

    appliedCodes(cart) {
      return (cart.discount_codes || [])
        .filter((entry) => entry.applicable !== false)
        .map((entry) => String(entry.code || '').toUpperCase())
        .filter(Boolean);
    },

    getSections() {
      const cartItems = document.querySelector('cart-items');
      if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return [];
      return [...new Set(cartItems.getSectionsToRender().map((section) => section.section))];
    },

    updateSections(sectionsHtml) {
      const cartItems = document.querySelector('cart-items');
      if (!cartItems || typeof cartItems.getSectionsToRender !== 'function' || !sectionsHtml) {
        return false;
      }

      const getHTML = cartItems.constructor.getElementHTML
        || (window.CartItems && window.CartItems.getElementHTML);

      cartItems.getSectionsToRender().forEach((section) => {
        const sectionEl = document.getElementById(section.id);
        if (!sectionEl || !sectionsHtml[section.section]) return;

        const el = sectionEl.querySelector(section.selector) || sectionEl;
        if (typeof getHTML === 'function') {
          el.innerHTML = getHTML(sectionsHtml[section.section], section.selector);
        }
      });

      return true;
    },

    async refreshCartUI() {
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refresh === 'function') {
        await cartItems.refresh();
        return;
      }

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    },

    async applyCartResult(data) {
      if (data && data.sections) {
        this.updateSections(data.sections);
      } else {
        await this.refreshCartUI();
      }
    },

    async addGiftProduct(variantId) {
      const sections = this.getSections();
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: { [GIFT_PROPERTY]: '1' }
          }],
          sections,
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || response.status);
      }

      await this.applyCartResult(data);
      return data;
    },

    async removeGiftLine(giftLine) {
      if (!giftLine) return null;

      const sections = this.getSections();
      const response = await fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: giftLine.key,
          quantity: 0,
          sections,
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || response.status);
      }

      await this.applyCartResult(data);
      return data;
    },

    updateOfferAppliedState(config, desired) {
      const qtyOfferEl = document.querySelector('#offer-notice .st-cart__offer');
      if (!config || !qtyOfferEl) return;

      const qtyCode = this.qtyCode(config);
      const giftActive = Boolean(
        desired
        && config.giftCode
        && desired.toUpperCase() === config.giftCode.toUpperCase()
      );
      const qtyActive = Boolean(
        desired
        && qtyCode
        && desired.toUpperCase() === qtyCode.toUpperCase()
      );

      qtyOfferEl.classList.toggle('is-applied', qtyActive);
      qtyOfferEl.classList.toggle('is-gift-active', giftActive);

      const qtyMsg = qtyOfferEl.querySelector('[data-offer-msg="qty"]');
      const giftMsg = qtyOfferEl.querySelector('[data-offer-msg="gift"]');
      if (qtyMsg) qtyMsg.hidden = giftActive;
      if (giftMsg) giftMsg.hidden = !giftActive;
    },

    async syncDiscount(config) {
      const desired = this.desiredCode(config);
      this.updateOfferAppliedState(config, desired);
      const desiredUpper = desired.toUpperCase();
      const offerCodes = this.offerCodes(config);

      if (!desired && offerCodes.length === 0) {
        this.updateOfferAppliedState(config, desired);
        this.syncGiftUI(config);
        return;
      }

      const cart = await this.fetchCart();
      const applied = this.appliedCodes(cart);
      const appliedOfferCodes = applied.filter((code) => offerCodes.includes(code));
      const isCorrect = desired
        ? appliedOfferCodes.length === 1 && appliedOfferCodes[0] === desiredUpper
        : appliedOfferCodes.length === 0;

      if (isCorrect) {
        this.updateOfferAppliedState(config, desired);
        this.syncGiftUI(config);
        return;
      }

      const sections = this.getSections();
      const response = await fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: desired,
          sections,
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.description || response.status);

      await this.applyCartResult(data);

      const nextConfig = this.getConfig() || config;
      this.syncGiftUI(nextConfig);
      this.updateOfferAppliedState(nextConfig, this.desiredCode(nextConfig));
    },

    async sync() {
      if (this.syncing) return;

      const config = this.getConfig();
      if (!config) return;

      try {
        this.syncing = true;

        let cart = await this.fetchCart();
        const giftLine = this.findGiftLineItem(cart);

        if (!config.giftUnlocked) {
          this.setGiftSelected(false);
          if (giftLine) {
            await this.removeGiftLine(giftLine);
            cart = await this.fetchCart();
          }
        } else if (giftLine) {
          this.setGiftSelected(true);
        } else if (config.giftVariantId && this.getGiftSelected()) {
          // Selected in storage but product missing — clear selection
          this.setGiftSelected(false);
        }

        this.syncGiftUI(config);
        await this.syncDiscount(this.getConfig() || config);
      } catch (error) {
        console.warn('[offer-notice] Failed to sync discount code', error); // eslint-disable-line no-console
      } finally {
        setTimeout(() => {
          this.syncing = false;
        }, 400);
      }
    },

    async claimGift() {
      const config = this.getConfig();
      if (!config || !config.giftUnlocked || !config.giftCode || !config.giftVariantId) return;
      if (this.syncing) return;

      try {
        this.syncing = true;

        if (config.giftVariantId) {
          const cart = await this.fetchCart();
          const existing = this.findGiftLineItem(cart);
          if (!existing) {
            await this.addGiftProduct(config.giftVariantId);
          }
        }

        this.setGiftSelected(true);
        this.syncGiftUI(config);
        await this.syncDiscount(this.getConfig() || config);
      } catch (error) {
        console.warn('[offer-notice] Failed to claim gift', error); // eslint-disable-line no-console
        this.setGiftSelected(false);
        this.syncGiftUI(config);
      } finally {
        setTimeout(() => {
          this.syncing = false;
        }, 400);
      }
    },

    async removeGift() {
      if (this.syncing) return;

      const config = this.getConfig();

      try {
        this.syncing = true;
        this.setGiftSelected(false);

        const cart = await this.fetchCart();
        const giftLine = this.findGiftLineItem(cart);
        if (giftLine) {
          await this.removeGiftLine(giftLine);
        }

        const nextConfig = this.getConfig() || config;
        this.syncGiftUI(nextConfig);
        await this.syncDiscount(nextConfig);
      } catch (error) {
        console.warn('[offer-notice] Failed to remove gift', error); // eslint-disable-line no-console
      } finally {
        setTimeout(() => {
          this.syncing = false;
        }, 400);
      }
    },

    bind() {
      if (this.bound) return;
      this.bound = true;

      document.addEventListener('click', (event) => {
        const claimBtn = event.target.closest('[data-gift-claim]');
        if (claimBtn) {
          event.preventDefault();
          this.claimGift();
          return;
        }

        const removeBtn = event.target.closest('[data-gift-remove]');
        if (removeBtn) {
          event.preventDefault();
          this.removeGift();
          return;
        }

        const applyQtyBtn = event.target.closest('[data-offer-apply-qty]');
        if (applyQtyBtn) {
          event.preventDefault();
          this.removeGift();
        }
      });
    }
  };

  window.OfferNotice = OfferNotice;
  OfferNotice.bind();

  const scheduleSync = (delay = 150) => {
    setTimeout(() => OfferNotice.sync(), delay);
  };

  document.addEventListener('on:line-item:change', () => scheduleSync(150));
  document.addEventListener('on:cart:add', () => scheduleSync(850));
  document.addEventListener('DOMContentLoaded', () => scheduleSync(300));

  if (document.readyState !== 'loading') scheduleSync(300);
})();
